/**
 * Client-side mirror of backend/src/services/promotions/promotionPricing.ts's
 * `priceFromDiscount` — the exact same rounding rules — so the promotion
 * builder can show the owner a live "customer pays / you save" preview as
 * they type, without a network round trip per keystroke.
 *
 * Deliberately its own module, not part of services/api/promotionsApi.ts:
 * that module is what PromotionBuilder's tests `vi.mock(...)` wholesale, which
 * would replace this pure function with a mock too. Keeping it here also
 * underlines what it is NOT — this is PRICE math only, what the customer will
 * pay. Cost and margin stay server-only (see promotionsApi.getPromotionMargin);
 * the server owns that arithmetic, this function never touches it.
 */

export type PromotionDiscountType = 'percentage' | 'amount' | 'fixed_price';

export interface PromotionDiscountInput {
  type: PromotionDiscountType;
  value: number;
}

export interface PromotionPricePreview {
  promoCents: number;
  savingCents: number;
  percentOff: number;
}

export function previewPriceFromDiscount(
  listCents: number,
  discount: PromotionDiscountInput,
): PromotionPricePreview {
  const list = Math.max(0, Math.round(listCents));
  let promo = list;

  if (discount.type === 'percentage') {
    promo = Math.round(list * (1 - discount.value / 100));
  } else if (discount.type === 'amount') {
    promo = list - Math.round(discount.value * 100);
  } else {
    promo = Math.round(discount.value * 100);
  }

  promo = Math.min(list, Math.max(0, promo));
  const saving = list - promo;
  const percentOff = list === 0 ? 0 : Math.round((saving / list) * 1000) / 10;

  return { promoCents: promo, savingCents: saving, percentOff };
}
